import React, { useState, useEffect } from "react";
import { formatUnits, parseUnits } from "ethers";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  addToast
} from "@heroui/react";

// import tradeABI from "../../abis/trade.json";
import ApprovePlatformInteraction from "../../utils/platformInteraction";
import { buyAsset } from "../../utils/buyAsset";
import { useWallet } from "../../context/WalletContext";
import { useReadContract } from "wagmi";
import TriggerPurchaseProcess from "../../utils/triggerPurchaseProcess";

const BuyNFTPopup = ({
  // onClose = () => { },
  isOpen,
  nftAddress,
  buyerPlatformFee,
  sellerPlatformFee,
  erc20BAFAddress,
  tradeContractAddress,
  transferProxyAddress,
  onOpenChange = () => {},
  metadata,
  item,
  user_balance,
  fetchNftData = () => {}
}) => {
  const BAF_TOKEN_ADDRESS = process.env.REACT_APP_BAF_TOKEN_ADDRESS;

  const {
    isConnected,
    address,
    bafBalanceFormatted,
    refreshBalance,
    publicClient,
    isCustodialWallet
  } = useWallet();

  const [balanceValue, setBalanceValue] = useState(0);

  useEffect(() => {
    if (isCustodialWallet) {
      setBalanceValue(user_balance ?? 0);
      return;
    } else {
      setBalanceValue(bafBalanceFormatted ?? 0);
      return;
    }
  }, [bafBalanceFormatted, user_balance, isCustodialWallet]);

  const [buyQuantity, setBuyQuantity] = useState("1");

  const [step, setStep] = useState(1);
  const [approvalStatus, setApprovalStatus] = useState("yetToStart");
  const [buyStatus, setBuyStatus] = useState("yetToStart");

  const collection_type = item?.data?.collection?.collection_type;

  const buyPrice = Number(item?.data?.collection?.instant_sale_price);
  const resalePrice = Number(item?.data?.collection?.resale_price);
  const displayPrice = buyPrice > 0 ? buyPrice : resalePrice;
  const collectionId = item?.data?.collection?.address;

  const unitPrice = resalePrice
    ? parseUnits(resalePrice?.toString(), 18)
    : parseUnits(buyPrice?.toString(), 18);

  const formattedUnitPrice = parseFloat(formatUnits(unitPrice, 18));
  const formattedBalance = Number(balanceValue).toFixed(2);

  const numericUnitPrice =
    typeof formattedUnitPrice === "string"
      ? parseFloat(formattedUnitPrice.replace(/[^0-9.-]+/g, ""))
      : formattedUnitPrice;

  const buyerFee = (numericUnitPrice * buyerPlatformFee) / 100;

  const totalValue = Number(resalePrice)
    ? Number(resalePrice) + Number(buyerFee)
    : Number(buyPrice) + Number(buyerFee);

  const formattedTotalValue = parseUnits(totalValue.toString(), 18);

  const formattedTotalPrice = formattedUnitPrice + buyerFee;

  const { data, isLoading, refetch, error } = useReadContract({
    abi: [
      {
        type: "function",
        name: "balanceOf",
        stateMutability: "view",
        inputs: [{ name: "account", type: "address" }],
        outputs: [{ type: "uint256" }]
      }
    ],
    address: BAF_TOKEN_ADDRESS,
    functionName: "balanceOf",
    args: address ? [address] : undefined
  });

  useEffect(() => {
    if (approvalStatus === "done" && buyStatus === "yetToStart") {
      setTimeout(() => {
        initPurchaseProcess();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStatus, buyStatus]);

  const checkBAFToken = async () => {
    try {
      const totalPrice = Number(formattedUnitPrice) + Number(buyerFee);

      if (isCustodialWallet) {
        const custodialBalance = Number(user_balance ?? 0);

        if (custodialBalance < totalPrice) {
          addToast({
            title: "Insufficient BAF",
            description: `You need at least ${totalPrice} BAF tokens to buy.`,
            color: "error",
            variant: "bordered"
          });
          return false;
        }

        return true;
      }

      if (!isConnected || (!address && !isCustodialWallet)) {
        addToast({
          title: "Connect Wallet",
          description: "Please connect your wallet to buy NFT.",
          color: "warning",
          variant: "bordered"
        });
        return false;
      }

      const res = await refetch();
      const latestData = res?.data;

      if (!latestData) {
        addToast({
          title: "Balance Fetch Failed",
          description: "Could not retrieve BAF balance. Please try again.",
          color: "error",
          variant: "bordered"
        });
        return false;
      }

      const latestBalance = Number(formatUnits(latestData ?? latestData, 18));

      if (latestBalance < totalPrice) {
        addToast({
          title: "Insufficient BAF",
          description: `You need at least ${totalPrice} BAF tokens to buy.`,
          color: "error",
          variant: "bordered"
        });
        return false;
      }

      return true;
    } catch (err) {
      console.error("Error checking BAF token:", err);
      addToast({
        title: "Token Check Failed",
        description: "Could not verify BAF token balance.",
        color: "error",
        variant: "bordered"
      });
      return false;
    }
  };

  const checkAndProcess = async () => {
    const hasBAF = await checkBAFToken();
    if (hasBAF && !isCustodialWallet) {
      setTimeout(() => {
        ApprovePlatformInteraction(
          erc20BAFAddress,
          transferProxyAddress,
          formattedTotalValue,
          setApprovalStatus,
          address
        );
      }, 0);
    } else if (hasBAF && isCustodialWallet) {
      setTimeout(() => {
        TriggerPurchaseProcess({
          erc20BAFAddress,
          transferProxyAddress,
          formattedTotalValue,
          setApprovalStatus,
          refreshBalance,
          fetchNftData,
          publicClient
        });
      }, 0);
    } else {
      addToast({
        title: "Insufficient BAF",
        description: "You need more BAF to buy.",
        color: "error"
      });
    }
  };

  const initPurchaseProcess = async () => {
    try {
      const result = await buyAsset({
        tradeContractAddress,
        address,
        collectionId,
        erc20BAFAddress,
        buyQuantity,
        metadata,
        nftAddress,
        item,
        setBuyStatus,
        buyerPlatformFee,
        publicClient,
        isCustodialWallet
      });

      if (result?.success === false) {
        console.log("Purchase Failed");
      } else {
        addToast({
          title: "Purchase Successful!",
          description: "NFT Purchase Successful!",
          color: "success",
          variant: "bordered"
        });
        await refreshBalance();
        fetchNftData();
      }
      // onclose();
    } catch (error) {
      console.log("🚀 ~ initPurchaseProcess ~ error:", error);
      addToast({
        title: "Purchase Failed",
        description: "An unexpected error occurred during the NFT purchase.",
        color: "danger",
        variant: "bordered"
      });
    }
  };

  const handleApproval = () => {
    setStep(2);
    checkAndProcess();
  };

  return (
    <>
      <Modal
        isDismissable={step === 1 || buyStatus === "failed"}
        isKeyboardDismissDisabled={step === 2 && buyStatus !== "failed"}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="xl"
        radius="md"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                {step === 1 ? "Checkout" : "Follow Steps"}
              </ModalHeader>
              <ModalBody>
                {step === 1 && (
                  <div className="flex flex-col gap-6">
                    <Input
                      label="Quantity"
                      labelPlacement="outside"
                      type="number"
                      value={buyQuantity}
                      variant="bordered"
                      readOnly={collection_type === "single" ? true : false}
                      onChange={(e) => {
                        let value = e.target.value;

                        if (value === "" || Number(value) < 1) {
                          value = "1";
                        }

                        if (Number(value) > 10) {
                          value = "10";
                        }
                        setBuyQuantity(value);
                      }}
                      classNames={{
                        label:
                          "block text-sm font-medium text-[#6A6A6A] group-data-[filled-within=true]:text-[#6A6A6A] group-data-[filled-within=true]:-translate-y-[calc(100%_+_1.5rem/2_+_20px)]",
                        inputWrapper:
                          "h-11 !appearance-none block bg-transparent data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent shadow-none w-full px-3 py-2 border border-[#C2C2C2] data-[hover=true]:border-[#C2C2C2] group-data-[focus=true]:border-[#C2C2C2] rounded-lg focus:outline-none",
                        input:
                          "text-base font-medium text-[#6A6A6A] placeholder-[#6A6A6A] !appearance-none"
                      }}
                    />

                    <Input
                      isReadOnly
                      label="Wallet Balance"
                      labelPlacement="outside"
                      type="text"
                      value={formattedBalance}
                      classNames={{
                        label:
                          "group-data-[filled-within=true]:-translate-y-[calc(100%_+_1.5rem/2_+_20px)]",
                        inputWrapper: "h-11"
                      }}
                    />

                    <Input
                      isReadOnly
                      label="Buy Price"
                      labelPlacement="outside"
                      type="text"
                      value={buyQuantity * displayPrice}
                      // onChange={(e) => setBuyQuantity(e.target.value)}
                      classNames={{
                        label:
                          "group-data-[filled-within=true]:-translate-y-[calc(100%_+_1.5rem/2_+_20px)]",
                        inputWrapper: "h-11"
                      }}
                    />

                    <Input
                      isReadOnly
                      label="Service Fee"
                      labelPlacement="outside"
                      type="text"
                      value={parseFloat(buyerFee).toFixed(4)}
                      classNames={{
                        label:
                          "group-data-[filled-within=true]:-translate-y-[calc(100%_+_1.5rem/2_+_20px)]",
                        inputWrapper: "h-11"
                      }}
                    />
                    <Input
                      isReadOnly
                      label="You will pay"
                      labelPlacement="outside"
                      type="text"
                      value={parseFloat(formattedTotalPrice).toFixed(4)}
                      classNames={{
                        label:
                          "group-data-[filled-within=true]:-translate-y-[calc(100%_+_1.5rem/2_+_20px)]",
                        inputWrapper: "h-11"
                      }}
                    />
                  </div>
                )}
                {step === 2 && (
                  <div className="w-full max-w-xs mx-auto">
                    <ol className="overflow-hidden flex flex-col gap-20 w-full">
                      <li
                        className={`w-full relative flex-1 after:content-[''] after:w-0.5 after:z-[-1] after:h-[9rem] after:inline-block after:absolute after:-bottom-[7rem] after:left-4 ${
                          approvalStatus === "done"
                            ? "after:bg-yellowbuy"
                            : "after:bg-gray-200"
                        }`}
                      >
                        <div className="flex items-center font-medium w-full">
                          <span
                            className={`w-8 h-8 ${
                              approvalStatus === "done"
                                ? "bg-yellowbuy border-2 border-yellowbuy"
                                : "bg-gray-50 border-2 border-gray-200"
                            } text-black rounded-full flex justify-center items-center mr-3 text-sm lg:w-10 lg:h-10`}
                          >
                            {approvalStatus === "yetToStart" ? (
                              1
                            ) : approvalStatus === "process" ? (
                              <span
                                className="animate-spin inline-block size-4 border-2 border-current border-t-transparent text-black rounded-full"
                                role="status"
                                aria-label="loading"
                              >
                                <span className="sr-only">Loading...</span>
                              </span>
                            ) : (
                              <svg
                                className="w-5 h-5 stroke-black"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 12L9.28722 16.2923C9.62045 16.6259 9.78706 16.7927 9.99421 16.7928C10.2014 16.7929 10.3681 16.6262 10.7016 16.2929L20 7"
                                  stroke="black"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </span>
                          <div className="flex flex-col gap-3">
                            <h4 className="text-base text-black">Approve</h4>
                            <Button
                              disabled={
                                approvalStatus === "process" ||
                                approvalStatus === "done"
                              }
                              isLoading={approvalStatus === "process"}
                              className="bg-yellowbuy border border-black text-black rounded-lg disabled:opacity-50 disabled:bg-[#D3D3D3]"
                              radius="none"
                            >
                              {approvalStatus === "yetToStart"
                                ? "Yet To Start"
                                : approvalStatus === "process"
                                  ? "Processing..."
                                  : approvalStatus === "done"
                                    ? "Approved"
                                    : "Yet To Start"}
                            </Button>
                          </div>
                        </div>
                      </li>
                      <li className="relative flex-1">
                        <div className="flex items-center font-medium w-full">
                          <span className="w-8 h-8 bg-gray-50 border-2 border-gray-200 text-black rounded-full flex justify-center items-center mr-3 text-sm lg:w-10 lg:h-10">
                            {buyStatus === "yetToStart" ? (
                              2
                            ) : buyStatus === "process" ? (
                              <span
                                className="animate-spin inline-block size-4 border-2 border-current border-t-transparent text-black rounded-full"
                                role="status"
                                aria-label="loading"
                              >
                                <span className="sr-only">Loading...</span>
                              </span>
                            ) : buyStatus === "failed" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="size-6 w-6 h-6 text-black"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-5 h-5 stroke-black"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 12L9.28722 16.2923C9.62045 16.6259 9.78706 16.7927 9.99421 16.7928C10.2014 16.7929 10.3681 16.6262 10.7016 16.2929L20 7"
                                  stroke="black"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </span>
                          <div className="flex flex-col gap-3">
                            <h4 className="text-base text-gray-900">
                              Purchase
                            </h4>
                            <Button
                              className="bg-yellowbuy border border-black text-black rounded-lg disabled:opacity-50 disabled:bg-[#D3D3D3]"
                              radius="none"
                              onPress={() => {
                                if (buyStatus === "done") {
                                  onClose();
                                } else if (buyStatus === "failed") {
                                  initPurchaseProcess();
                                }
                              }}
                            >
                              {buyStatus === "yetToStart"
                                ? "Yet To Start"
                                : buyStatus === "process"
                                  ? "Processing..."
                                  : buyStatus === "done"
                                    ? "Done"
                                    : buyStatus === "failed"
                                      ? "Try again"
                                      : "Yet To Start"}
                            </Button>
                          </div>
                        </div>
                      </li>
                    </ol>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                {step === 1 && (
                  <Button
                    color="primary"
                    radius="none"
                    className="bg-yellowbuy text-black px-16 mx-auto border border-black rounded-lg max-w-[13.75rem] w-full"
                    onPress={handleApproval}
                  >
                    Approve
                  </Button>
                )}
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default BuyNFTPopup;
